import { Building2 } from 'lucide-react';
import type { PlanOfCareReviewViewModel } from '@generated';
import { addDays } from 'date-fns';
import { SmallSection, SmallSectionFieldRow } from '~/shared/section/SmallSection';
import { Section } from '~/shared/section/Section';
import { SectionTitle } from '~/shared/section/SectionTitle';

export function ReferralTab({ review }: { review: PlanOfCareReviewViewModel }) {
	const referral = review.data.referral;
	const primaryPayor = review.data.insurance.payors.find(x => x.order === 1)!;
	const startOfCareDate = review.data.admission.startOfCareDate;
	const relevantHospitalizations = review.data.historicalHospitalizations
		.filter(h => addDays(h.dischargeDate, 14) >= startOfCareDate)
		.sort((a, b) => b.dischargeDate.getTime() - a.dischargeDate.getTime());
	const priorHospStay = relevantHospitalizations.length > 0 ? relevantHospitalizations[0] : null;
	return (
		<div className="space-y-3">
			<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
				<Section header={<SectionTitle title="Referral Information" />}>
					<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4">
						<SmallSectionFieldRow label="Referral Source" value={referral.admissionSourceLabel} />
						<SmallSectionFieldRow label="Referral Date" value={referral.referralDate.toLocaleDateString()} />
						<SmallSectionFieldRow label="Referring Physician" value={referral.referringPhysician.fullName} />
						<SmallSectionFieldRow label="Physician NPI" value={referral.referringPhysician.npiNumber} />
						<SmallSectionFieldRow label="Referring Facility" value={referral.referringFacilityName} span />
					</dl>
				</Section>
				<SmallSection title="Insurance / Payer">
					<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4">
						<SmallSectionFieldRow label="Payer" value={primaryPayor.insuranceProviderName} />
						<SmallSectionFieldRow label="Member ID" value={primaryPayor.memberId} />
						<SmallSectionFieldRow label="Group Number" value={primaryPayor.groupId} />
						<div className="col-span-2 border-t border-gray-50 pt-1">
							<dt className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
								Prior Inpatient Stay (within 14 days)
							</dt>
							{priorHospStay ? (
								<div className="space-y-2 rounded bg-firstSnow p-3">
									<div className="flex items-center gap-1.5 text-xs font-semibold text-thunderNight">
										<Building2 size={13} />
										Yes — Prior hospitalization documented
									</div>
									<dl className="grid grid-cols-3 gap-3 text-xs">
										<SmallSectionFieldRow label="Facility" value={priorHospStay.facilityName} />
										<SmallSectionFieldRow label="Admit Date" value={priorHospStay.admissionDate.toLocaleDateString()} />
										<SmallSectionFieldRow label="Discharge Date" value={priorHospStay.dischargeDate.toLocaleDateString()} />
									</dl>
								</div>
							) : (
								<span className="text-xs text-gray-500">No prior inpatient stay within 14 days</span>
							)}
						</div>
					</dl>
				</SmallSection>
			</div>
			<SmallSection title="Referring Diagnoses">
				<div className="space-y-2 p-4">
					<div className="flex items-start gap-3 rounded border border-cloudless bg-firstSnow p-2.5">
						<span className="shrink-0 rounded bg-thunderNight px-2 py-0.5 text-[10px] font-semibold text-white">Primary</span>
						<div>
							<span className="text-xs font-semibold text-thunderNight">{referral.referringDiagnosis.code}</span>
							<span className="ml-2 text-xs text-gray-700">{referral.referringDiagnosis.description}</span>
						</div>
					</div>
				</div>
			</SmallSection>
			<SmallSection title="Notes">
				<p className="px-4 py-3 text-xs leading-relaxed text-gray-700">{referral.notes ?? 'No notes'}</p>
			</SmallSection>
		</div>
	);
}
